import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AccountInterface,
  AccountSearchHint,
  AccountSearchResponse,
  AccountSearchStatus,
} from "../interfaces";
import {
  searchAccountsLive,
  probeAccountSearchV2,
  getMinTokenLength,
} from "../services/accountServices";
import {
  getCachedAccountSearch,
  setCachedAccountSearch,
} from "../utils/accountSearchCache";
import {
  normalizeSearch,
  NormalizedSearch,
  SEARCH_STRINGS,
} from "../utils/searchText";

/**
 * Un segundo, no 400 ms: mientras ApiCore no propague la cancelación al SP
 * (pata 1, verificado en `sys.dm_exec_requests`), un rebote más corto sólo
 * multiplica consultas que la base de datos ya no puede abortar.
 */
export const LIVE_SEARCH_DEBOUNCE_MS = 1000;

export interface UseAccountSearchOptions {
  debounceMs?: number;
  pageSize?: number;
  withAgreementAuths?: boolean;
  /** Se aplica al pintar, no al guardar: la caché guarda lo que devolvió el API. */
  filter?: (account: AccountInterface) => boolean;
}

export interface SetQueryOptions {
  /**
   * Cambio de texto que no lo escribió el usuario (elegir un cliente, restaurar
   * al perder el foco). Marca la clave como ya consultada, así que no dispara
   * nada.
   */
  silent?: boolean;
}

export interface UseAccountSearch {
  query: string;
  results: AccountInterface[];
  loading: boolean;
  /** Hubo una tecla y hay una consulta agendada que todavía no sale. */
  pending: boolean;
  status: AccountSearchStatus;
  hint: AccountSearchHint;
  reason: string;
  error: string;
  normalized: NormalizedSearch;
  advancedAvailable: boolean;
  setQuery: (text: string, options?: SetQueryOptions) => void;
  searchNow: () => void;
  cancel: () => void;
  reset: () => void;
}

export const useAccountSearch = (
  options: UseAccountSearchOptions = {}
): UseAccountSearch => {
  const {
    debounceMs = LIVE_SEARCH_DEBOUNCE_MS,
    pageSize = 20,
    withAgreementAuths = true,
    filter,
  } = options;

  const [query, setQueryState] = useState("");
  const [rawResults, setRawResults] = useState<AccountInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<AccountSearchStatus>("Ok");
  const [responseHint, setResponseHint] = useState<AccountSearchHint>("None");
  const [reason, setReason] = useState("None");
  const [error, setError] = useState("");
  const [advancedAvailable, setAdvancedAvailable] = useState(false);
  const [minTokenLength, setMinTokenLength] = useState(getMinTokenLength());

  const normalized = useMemo(
    () => normalizeSearch(query, { minTokenLength }),
    [query, minTokenLength]
  );

  // Última clave que ya se consultó (o que se dio por consultada en un cambio
  // silencioso). Es lo que evita repetir la misma búsqueda.
  const lastKeyRef = useRef("");
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retriedKeyRef = useRef("");

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const applyResponse = useCallback(
    (response: AccountSearchResponse) => {
      setStatus(response.status);
      setResponseHint(response.hint);
      setReason(response.reason);

      if (response.minTokenLength) {
        setMinTokenLength(response.minTokenLength);
      }

      if (response.didError) {
        // Se conserva lo que ya estaba en pantalla: un fallo no debe borrar
        // resultados válidos debajo del cursor.
        setError(response.errorMessage || SEARCH_STRINGS.error);
        return;
      }

      setError("");
      setRawResults(response.model ?? []);
    },
    []
  );

  const run = useCallback(
    async (target: NormalizedSearch) => {
      clearTimer();

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      lastKeyRef.current = target.key;
      setPending(false);

      const cached = getCachedAccountSearch(target.key);
      if (cached) {
        applyResponse(cached);
        return;
      }

      setLoading(true);

      try {
        const response = await searchAccountsLive(
          target,
          { pageSize, withAgreementAuths },
          controller.signal
        );

        // Respuesta vieja: llegó después de que otra tecla la reemplazara.
        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return;
        }

        // 429: se conserva lo mostrado y se reintenta una vez tras un rebote.
        if (response.rateLimited && retriedKeyRef.current !== target.key) {
          retriedKeyRef.current = target.key;
          setLoading(false);
          setPending(true);
          timerRef.current = setTimeout(() => {
            run(target);
          }, debounceMs);
          return;
        }

        setCachedAccountSearch(target.key, response);
        applyResponse(response);
      } catch (searchError) {
        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return;
        }
        setError(SEARCH_STRINGS.error);
      } finally {
        if (!controller.signal.aborted && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [applyResponse, clearTimer, debounceMs, pageSize, withAgreementAuths]
  );

  useEffect(() => {
    const key = normalized.key;

    // Nada que consultar: se limpia la lista y se suelta la clave, para que
    // volver a escribir el mismo texto sí vuelva a buscar.
    if (!key) {
      clearTimer();
      lastKeyRef.current = "";
      setPending(false);
      setRawResults([]);
      setError("");
      setResponseHint("None");
      setStatus(normalized.route === "BelowMinimum" ? "BelowMinimum" : "Ok");
      setReason(normalized.reason);
      return;
    }

    if (key === lastKeyRef.current) {
      setPending(false);
      return;
    }

    setPending(true);
    timerRef.current = setTimeout(() => {
      run(normalized);
    }, debounceMs);

    return clearTimer;
  }, [normalized, debounceMs, run, clearTimer]);

  useEffect(() => {
    let alive = true;

    probeAccountSearchV2().then((available) => {
      if (!alive) {
        return;
      }
      setAdvancedAvailable(available);
      setMinTokenLength(getMinTokenLength());
    });

    return () => {
      alive = false;
    };
  }, []);

  // Al desmontar no debe quedar ni un temporizador ni una petición viva.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const setQuery = useCallback(
    (text: string, setOptions: SetQueryOptions = {}) => {
      if (setOptions.silent) {
        lastKeyRef.current = normalizeSearch(text, { minTokenLength }).key;
        setPending(false);
      }
      setQueryState(text);
    },
    [minTokenLength]
  );

  const cancel = useCallback(() => {
    clearTimer();
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setPending(false);
    setLoading(false);
  }, [clearTimer]);

  const searchNow = useCallback(() => {
    if (!normalized.key) {
      return;
    }
    run(normalized);
  }, [normalized, run]);

  const reset = useCallback(() => {
    cancel();
    lastKeyRef.current = "";
    retriedKeyRef.current = "";
    setQueryState("");
    setRawResults([]);
    setError("");
    setStatus("Ok");
    setResponseHint("None");
    setReason("None");
  }, [cancel]);

  const results = useMemo(
    () => (filter ? rawResults.filter(filter) : rawResults),
    [rawResults, filter]
  );

  // El texto actual manda sobre la pista que vino en la respuesta anterior.
  const hint = normalized.hint !== "None" ? normalized.hint : responseHint;

  return {
    query,
    results,
    loading,
    pending,
    status,
    hint,
    reason,
    error,
    normalized,
    advancedAvailable,
    setQuery,
    searchNow,
    cancel,
    reset,
  };
};
